// Port of generate_invoice.py's build() to exceljs. Same cells, merges,
// widths, heights, fills, number formats and totals formulas; the reference
// script wins on any difference. ExcelJS is passed in so the browser can
// load it lazily (it is large) and tests can use it directly.
import {
  INVOICE_COLOURS as C, INVOICE_FONT, INVOICE_FONT_BOLD, USD_FORMAT, DATE_FORMAT, COLUMN_WIDTHS,
  FROM_DEFAULT, PAYMENT_DEFAULT, PAYMENT_TERMS, FOOTER, HEADER_ROW, LOGO_SIZE, LINE_KINDS, kindOf,
} from "./house";
import { amountValue, subtotal } from "./amount";

const argb = hex => ({ argb: `FF${hex}` });
const font = ({ size = 9, bold = false, color = C.body } = {}) => ({ name: bold ? INVOICE_FONT_BOLD : INVOICE_FONT, size, bold, color: argb(color) });
const solid = hex => ({ type: "pattern", pattern: "solid", fgColor: argb(hex) });

// "2026-10-16" → a Date that Excel reads as 16 Oct 2026 in any time zone.
const excelDate = iso => { const [y, m, d] = iso.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };

export function buildInvoiceWorkbook(ExcelJS, cfg, logoBase64) {
  const wb = new ExcelJS.Workbook();
  wb.calcProperties = { fullCalcOnLoad: true };
  const ws = wb.addWorksheet("Invoice", { views: [{ showGridLines: false }] });
  const cell = (addr, value, style = {}) => {
    const c = ws.getCell(addr);
    if (value !== undefined) c.value = value;
    if (style.font) c.font = style.font;
    if (style.alignment) c.alignment = style.alignment;
    if (style.numFmt) c.numFmt = style.numFmt;
    if (style.fill) c.fill = style.fill;
    return c;
  };

  Object.entries(COLUMN_WIDTHS).forEach(([col, w]) => { ws.getColumn(col).width = w; });
  "ABCDEFGHI".split("").forEach(col => cell(`${col}1`, undefined, { fill: solid(C.band) }));

  if (logoBase64) {
    const id = wb.addImage({ base64: logoBase64, extension: "png" });
    ws.addImage(id, { tl: { col: 1, row: 2 }, ext: LOGO_SIZE, editAs: "oneCell" });
  }
  ws.getRow(3).height = 50.2;
  cell("H3", "INVOICE", { font: font({ size: 18, color: C.black }), alignment: { horizontal: "right", vertical: "middle" } });

  const frm = { ...FROM_DEFAULT, ...(cfg.from || {}) };
  const grey = font({ color: C.greyText });
  const top = { vertical: "top" };
  cell("B5", frm.entity, { font: grey });
  cell("B7", frm.address, { font: grey, alignment: { wrapText: true, vertical: "top" } });
  ws.getRow(7).height = 58;
  cell("B8", "Contact:", { font: grey, alignment: top });
  cell("C8", frm.contact, { font: font() });
  cell("B9", "Website:", { font: grey, alignment: top });
  cell("C9", frm.website, { font: font() });

  const bt = cfg.bill_to || {};
  const label = font({ bold: true, color: C.darkLabel });
  cell("B13", "Invoice for:", { font: label });
  cell("F13", "Date:", { font: label });
  cell("G13", excelDate(cfg.issue_date), { font: font(), numFmt: DATE_FORMAT, alignment: { horizontal: "right" } });
  cell("B14", "Name:", { font: grey, alignment: top });
  cell("C14", bt.name || "", { font: font() });
  cell("F14", "Invoice Number: ", { font: label });
  cell("G14", cfg.invoice_number, { font: font(), alignment: { horizontal: "right" } });
  cell("B15", "Address: ", { font: grey, alignment: top });
  cell("C15", bt.address || "", { font: font(), alignment: { wrapText: true, vertical: "top" } });
  cell("B16", "Email:", { font: grey, alignment: top });
  cell("C16", bt.email || "", { font: font() });
  [13, 14, 15, 16].forEach(r => ws.mergeCells(`C${r}:E${r}`));

  // ---- line items ----
  const currency = cfg.currency || "USD";
  const hdr = HEADER_ROW;
  const pine = font({ bold: true, color: C.pine });
  cell(`B${hdr}`, "Description", { font: pine });
  cell(`H${hdr}`, "Total", { font: pine, alignment: { horizontal: "right" } });
  ws.mergeCells(`B${hdr}:D${hdr}`);

  const items = cfg.line_items || [];
  const first = hdr + 1;
  let r = first;
  const dataRows = [];
  LINE_KINDS.forEach(([kind, title]) => {
    const group = items.filter(it => kindOf(it) === kind);
    if (group.length === 0) return;
    cell(`B${r}`, title.toUpperCase(), { font: font({ size: 7.5, bold: true, color: C.pine }), alignment: { vertical: "bottom" } });
    ws.mergeCells(`B${r}:D${r}`);
    ws.getRow(r).height = 22;
    r += 1;
    group.forEach((it, i) => {
      const fill = solid(i % 2 === 0 ? C.altFill : C.white);
      "BCDEFGH".split("").forEach(col => cell(`${col}${r}`, undefined, { fill }));
      cell(`B${r}`, it.description, { font: font(), alignment: { wrapText: true, vertical: "middle" } });
      ws.mergeCells(`B${r}:D${r}`);
      cell(`F${r}`, currency, { font: grey });
      const amt = it.amount;
      if (amt !== undefined && amt !== null && amt !== "") {
        const value = typeof amt === "string" && amt.startsWith("=") ? { formula: amt.slice(1), result: amountValue(amt) } : amt;
        cell(`H${r}`, value, { numFmt: USD_FORMAT, alignment: { horizontal: "right" } });
      }
      dataRows.push(r);
      r += 1;
    });
  });
  const last = r - 1;

  // ---- totals ----
  const sub = last + 1, gst = sub + 1, total = gst + 2;
  const sum = subtotal(items);
  const right = { horizontal: "right" };
  cell(`F${sub}`, "Subtotal", { font: font({ color: C.pine }), alignment: right });
  cell(`H${sub}`, { formula: `SUM(H${first}:H${last})`, result: sum }, { font: font(), numFmt: USD_FORMAT, alignment: right });
  cell(`F${gst}`, "GST", { font: font({ color: C.pine }), alignment: right });
  cell(`H${gst}`, cfg.gst ?? "exempt", { font: font(), alignment: right });
  cell(`F${total}`, "Total", { font: pine, alignment: right });
  cell(`H${total}`, { formula: `H${sub}`, result: sum }, { font: pine, numFmt: USD_FORMAT, alignment: right });

  // ---- payment details ----
  const pay = cfg.payment_details || PAYMENT_DEFAULT;
  const greyBold = font({ bold: true, color: C.greyText });
  let pr = total + 3;
  cell(`B${pr}`, "Payment Details: ", { font: greyBold });
  cell(`C${pr}`, PAYMENT_TERMS, { font: grey, alignment: { horizontal: "left" } });
  pr += 1;
  pay.forEach(([l, v]) => {
    cell(`B${pr}`, l, { font: greyBold });
    cell(`C${pr}`, v, { font: grey, alignment: { horizontal: "left" } });
    pr += 1;
  });

  cell(`B${pr + 1}`, "Thank you!", { font: grey });
  const foot = pr + 3;
  cell(`B${foot}`, FOOTER, { font: font({ size: 8, color: C.greyText }) });
  ws.mergeCells(`B${foot}:H${foot}`);

  ws.pageSetup = { orientation: "portrait", fitToPage: true, fitToWidth: 1, fitToHeight: 1, printArea: `A1:I${foot + 1}` };
  return { wb, rows: { first, last, sub, gst, total, foot } };
}
